import { Flex, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import Table from "../../../../components/Table/Table.tsx";
import { TableRow } from "../../../../components/Table/types.ts";
import { InvoiceLine } from "../../../../types/invoice/invoice.ts";
import { toCurrencyPrice, toPercent } from "../../../../utils/number.ts";
import { useStyles } from "./InvoiceLinesModal.styles.ts";

interface Props {
  data: InvoiceLine[];
}

export const InvoiceLinesModal = ({ data }: Props) => {
  const { classes } = useStyles();

  const [opened, { open, close }] = useDisclosure(false);

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "product",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "product",
          }}
        >
          Product
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "label",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "label",
          }}
        >
          Label
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "quantity",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "quantity",
          }}
        >
          Quantity
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "uoM",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "uoM",
          }}
        >
          UoM
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "totalWeight",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "totalWeight",
          }}
        >
          Total Weight
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "price",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "price",
          }}
        >
          Price
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "taxes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "taxes",
          }}
        >
          Taxes
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "subtotal",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoiceDetail",
            fieldName: "subtotal",
          }}
        >
          Subtotal
        </DatabaseMetadataPopover>
      ),
    },
  ];

  const rows = useMemo(() => {
    return data.map((item) => ({
      ...item,
      price: toCurrencyPrice(item.price),
      taxes: toPercent(item.taxes, "percentage"),
      subtotal: toCurrencyPrice(item.subtotal),
    }));
  }, [data]);

  return (
    <>
      <Text className={classes.label} onClick={open}>
        Show
      </Text>
      <Modal
        title="Invoice Details"
        size="auto"
        opened={opened}
        onClose={close}
      >
        <Flex direction="column" gap={8}>
          <Table
            minWidth="350px"
            columns={columns}
            rows={rows}
            verticalAlignment="top"
          />
        </Flex>
      </Modal>
    </>
  );
};
